import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../api/client';
import { getRoleDisplayName } from '../utils/roleColors';
import { Shield, Loader2, ChevronLeft, BadgeCheck, Scale, Users, Settings, Upload } from 'lucide-react';

interface DemoPersona {
  id: string;
  name: string;
  email: string;
  role: string;
  department?: string;
}

const ROLE_DISPLAY: Record<string, string> = {
  LawEnforcement: 'Law Enforcement',
  Attorney: 'Attorney',
  PublicSubmitter: 'Public Submitter',
  CaseManager: 'Case Manager',
  SystemAdmin: 'System Admin',
};

const ROLE_ICONS: Record<string, typeof Shield> = {
  LawEnforcement: BadgeCheck,
  Attorney: Scale,
  PublicSubmitter: Upload,
  CaseManager: Users,
  SystemAdmin: Settings,
};

export default function Login() {
  const [personas, setPersonas] = useState<DemoPersona[]>([]);
  const [selectedPersona, setSelectedPersona] = useState<DemoPersona | null>(null);
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPersonas, setIsLoadingPersonas] = useState(true);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchDemoUsers() {
      try {
        const users = await apiClient.getDemoUsers();
        setPersonas(users.map(u => ({
          id: u.id,
          name: u.name,
          email: u.email,
          role: u.role,
          department: u.department,
        })));
      } catch (err) {
        console.error('Failed to fetch demo users:', err);
        setError('Failed to load demo users');
      } finally {
        setIsLoadingPersonas(false);
      }
    }
    fetchDemoUsers();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPersona) {
      setError('Please select a persona first');
      return;
    }
    if (pin.length !== 6) {
      setError('Please enter your 6-digit PIN');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await login(pin, selectedPersona.id);
      navigate('/');
    } catch {
      setError('Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectPersona = (persona: DemoPersona) => {
    setSelectedPersona(persona);
    setPin('');
    setError('');
  };

  const handleBack = () => {
    setSelectedPersona(null);
    setPin('');
    setError('');
  };

  const getDisplayRole = (role: string, department?: string) =>
    getRoleDisplayName(role, department) || ROLE_DISPLAY[role] || role;
  const getRoleIcon = (role: string) => ROLE_ICONS[role] || Shield;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 flex flex-col items-center justify-center p-4">
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-justice-gold/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-slate-700/20 rounded-full blur-3xl" />
      </div>

      <div className="w-full max-w-md relative z-10">
        {/* Logo and Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="p-4 bg-slate-800/80 rounded-2xl border border-slate-700/50 shadow-xl backdrop-blur-sm">
              <Shield className="h-12 w-12 text-justice-gold" />
            </div>
          </div>
          <h1 className="text-2xl font-semibold text-white tracking-tight">
            Digital Evidence Management System
          </h1>
          <p className="text-slate-400 mt-2 text-sm">
            Secure Access Portal
          </p>
        </div>

        {/* Login card */}
        <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl border border-slate-200/50 overflow-hidden">
          {isLoadingPersonas ? (
            <div className="flex flex-col items-center justify-center py-12 px-6">
              <Loader2 className="h-8 w-8 animate-spin text-slate-600" />
              <p className="text-sm text-slate-500 mt-3">Loading authorized users...</p>
            </div>
          ) : !selectedPersona ? (
            <div className="p-6">
              <div className="text-center mb-6">
                <h2 className="text-lg font-semibold text-slate-900">
                  Select Your Role
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                  Choose an authorized profile to continue
                </p>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700 text-center">{error}</p>
                </div>
              )}

              <div className="space-y-2">
                {personas.map((persona) => {
                  const displayRole = getDisplayRole(persona.role, persona.department);
                  const Icon = getRoleIcon(persona.role);
                  return (
                    <button
                      key={persona.id}
                      onClick={() => handleSelectPersona(persona)}
                      className="w-full flex items-center gap-4 px-4 py-3.5 rounded-xl border border-slate-200 bg-slate-50/50 hover:bg-slate-100 hover:border-slate-300 transition-all group"
                    >
                      <div className="p-2.5 rounded-lg bg-slate-800 text-justice-gold group-hover:bg-justice-navy transition-colors">
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="flex-1 text-left">
                        <p className="font-medium text-slate-900 text-sm">
                          {persona.name}
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {displayRole}
                          {persona.department && (
                            <span className="text-slate-400"> • {persona.department}</span>
                          )}
                        </p>
                      </div>
                      <ChevronLeft className="h-4 w-4 text-slate-400 rotate-180 group-hover:translate-x-0.5 transition-transform" />
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="p-6">
              <button
                onClick={handleBack}
                className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-5 -ml-1"
              >
                <ChevronLeft className="h-4 w-4" />
                Back to personas
              </button>

              {/* Selected user card */}
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-lg bg-slate-800 text-justice-gold">
                    {(() => {
                      const Icon = getRoleIcon(selectedPersona.role);
                      return <Icon className="h-5 w-5" />;
                    })()}
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">
                      {selectedPersona.name}
                    </p>
                    <p className="text-xs text-slate-500">
                      {getDisplayRole(selectedPersona.role)}
                    </p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label htmlFor="pin" className="block text-sm font-medium text-slate-700 mb-2">
                    Enter your 6-digit PIN
                  </label>
                  <input
                    id="pin"
                    type="password"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    value={pin}
                    onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                    className="block w-full px-4 py-3.5 text-center text-2xl tracking-[0.3em] border border-slate-300 rounded-xl focus:ring-2 focus:ring-slate-800 focus:border-slate-800 bg-white"
                    placeholder="••••••"
                    disabled={isLoading}
                    autoFocus
                  />
                </div>

                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-700 text-center">{error}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isLoading || pin.length !== 6}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3.5 bg-slate-800 text-white font-medium rounded-xl hover:bg-slate-900 focus:ring-2 focus:ring-offset-2 focus:ring-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Authenticating...
                    </>
                  ) : (
                    'Sign In'
                  )}
                </button>
              </form>
            </div>
          )}
        </div>

        <p className="mt-8 text-center text-xs text-slate-500">
          Authorized Personnel Only • Secure Demonstration Environment
        </p>
      </div>
    </div>
  );
}
